export interface ScriptOptions {
  /** URL скрипта */
  src: string;

  /** Асинхронная загрузка */
  async?: boolean;

  /** Отложенная загрузка */
  defer?: boolean;

  /** Тип скрипта (например, 'module') */
  type?: string;

  /** Для CORS запросов */
  crossOrigin?: 'anonymous' | 'use-credentials';

  /** Проверка целостности */
  integrity?: string;

  /** Политика referrer */
  referrerPolicy?: ReferrerPolicy;

  /** Подсказка для предзагрузки */
  fetchPriority?: 'high' | 'low' | 'auto';

  /** Nonce для CSP */
  nonce?: string;

  /** Не загружать повторно если уже есть */
  skipIfExists?: boolean;

  /** Куда вставлять: head или body */
  target?: 'head' | 'body';

  /** Произвольные data-атрибуты */
  dataset?: Record<string, string>;

  /** ID элемента */
  id?: string;
}

export interface ScriptLoadResult {
  element: HTMLScriptElement;
  cached: boolean;
}

export type ScriptInput = ScriptOptions | string;

/**
 * Загружает скрипт динамически
 * 
 * @param optionsOrSrc 
 * @returns 
 */
export function loadScript(optionsOrSrc: ScriptInput): Promise<ScriptLoadResult> {
  const options: ScriptOptions = typeof optionsOrSrc === 'string'
    ? { src: optionsOrSrc }
    : optionsOrSrc;

  const {
    src,
    async: isAsync = false,
    defer = false,
    type,
    crossOrigin,
    integrity,
    referrerPolicy,
    fetchPriority,
    nonce,
    skipIfExists = true,
    target = 'head',
    dataset,
    id,
  } = options;

  return new Promise((resolve, reject) => {
    // Проверяем, не загружен ли уже скрипт
    if (skipIfExists) {
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="${src}"]`
      );
      if (existingScript) {
        resolve({ element: existingScript, cached: true });
        return;
      }
    }

    const script = document.createElement('script');
    script.src = src;

    if (isAsync) script.async = true;
    if (defer) script.defer = true;
    if (type) script.type = type;
    if (crossOrigin) script.crossOrigin = crossOrigin;
    if (integrity) script.integrity = integrity;
    if (referrerPolicy) script.referrerPolicy = referrerPolicy;
    if (fetchPriority) script.fetchPriority = fetchPriority;
    if (nonce) script.nonce = nonce;
    if (id) script.id = id;

    if (dataset) {
      Object.entries(dataset).forEach(([key, value]) => {
        script.dataset[key] = value;
      });
    }

    script.onload = () => resolve({ element: script, cached: false });
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

    const targetElement = target === 'head' ? document.head : document.body;
    targetElement.appendChild(script);
  });
}

/**
 * Загружает несколько скриптов последовательно
 * 
 * @param scripts 
 * @returns 
 */
export async function loadScriptsSequentially(
  scripts: ScriptInput[]
): Promise<ScriptLoadResult[]> {
  const results: ScriptLoadResult[] = [];

  for (const script of scripts) {
    const result = await loadScript(script);
    results.push(result);
  }

  return results;
}

/**
 * Загружает несколько скриптов параллельно
 * 
 * @param scripts 
 * @returns 
 */
export function loadScriptsParallel(
  scripts: ScriptInput[]
): Promise<ScriptLoadResult[]> {
  return Promise.all(scripts.map((script) => loadScript(script)));
}

/**
 * Удаляет загруженный скрипт
 * 
 * @param src 
 * @returns 
 */
export function unloadScript(src: string): boolean {
  const script = document.querySelector<HTMLScriptElement>(
    `script[src="${src}"]`
  );
  if (script) {
    script.remove();
    return true;
  }
  return false;
}

/**
 * Предзагрузка скрипта (preload)
 * 
 * @param src 
 * @returns 
 */
export function preloadScript(src: string): HTMLLinkElement {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'script';
  link.href = src;
  document.head.appendChild(link);
  return link;
}
